import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import {
  ledgersApi,
  type InvestorCommitmentLedgerRow,
  type InvestorDistributionLedgerRow,
} from "@/api/ledgers";
import { Card, Button, Input } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  downloadCsv,
  formatDate,
  formatMoney,
} from "./ledgerHelpers";

/**
 * Payment System v3.0 PR 11 — Investor Ledger (design spec §10.7). All
 * commitments + distributions for one investor, with running balances and
 * YTD earnings.
 */
export function InvestorLedgerPage() {
  const { investorId } = useParams<{ investorId: string }>();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [poolFilter, setPoolFilter] = useState("");
  const [tierFilter, setTierFilter] = useState("");

  const query = useQuery({
    queryKey: ["ledger.investor", investorId, from, to],
    queryFn: () =>
      ledgersApi.investor(investorId!, {
        from: from || undefined,
        to: to || undefined,
      }),
    enabled: !!investorId,
  });

  const data = query.data;

  const filteredCommitments = useMemo(() => {
    if (!data) return [];
    return data.commitments.filter((c) => {
      if (poolFilter && !(c.poolName ?? c.poolId).toLowerCase().includes(poolFilter.toLowerCase())) return false;
      if (tierFilter && (c.tier ?? "").toLowerCase() !== tierFilter.toLowerCase()) return false;
      return true;
    });
  }, [data, poolFilter, tierFilter]);

  const commitmentIds = useMemo(
    () => new Set(filteredCommitments.map((c) => c.commitmentId)),
    [filteredCommitments],
  );

  const filteredDistributions = useMemo(() => {
    if (!data) return [];
    return data.distributions.filter((d) => commitmentIds.has(d.commitmentId));
  }, [data, commitmentIds]);

  const commitmentCols: Column<InvestorCommitmentLedgerRow>[] = [
    { key: "date", header: "Commitment Date", render: (c) => formatDate(c.commitmentDate) },
    { key: "pool", header: "Pool", render: (c) => c.poolName ?? c.poolId },
    { key: "tier", header: "Tier", render: (c) => c.tier ?? "—" },
    { key: "committed", header: "Committed", render: (c) => formatMoney(c.committedAmount) },
    { key: "funded", header: "Funded", render: (c) => formatMoney(c.fundedAmount) },
    {
      key: "outstanding",
      header: "Current Outstanding",
      render: (c) => formatMoney(c.currentPrincipalBalance),
    },
    { key: "status", header: "Status", render: (c) => c.status },
  ];

  const distributionCols: Column<InvestorDistributionLedgerRow>[] = [
    {
      key: "period",
      header: "Period",
      render: (d) => `${formatDate(d.periodStart)} → ${formatDate(d.periodEnd)}`,
    },
    { key: "gross", header: "Gross", render: (d) => formatMoney(d.grossAmount) },
    { key: "withheld", header: "Withheld", render: (d) => formatMoney(d.withheldAmount) },
    { key: "net", header: "Net", render: (d) => formatMoney(d.netAmount) },
    { key: "paid", header: "Paid", render: (d) => formatDate(d.paidAt) },
  ];

  function exportCsv() {
    if (!data) return;
    downloadCsv(
      `investor-ledger-${data.investorDisplayName ?? data.investorId}.csv`,
      [
        "Section",
        "Date",
        "Pool",
        "Tier",
        "Committed",
        "Funded",
        "Outstanding",
        "Gross",
        "Withheld",
        "Net",
        "Status",
      ],
      [
        ...filteredCommitments.map((c) => [
          "Commitment",
          c.commitmentDate,
          c.poolName ?? c.poolId,
          c.tier ?? "",
          c.committedAmount,
          c.fundedAmount,
          c.currentPrincipalBalance,
          "",
          "",
          "",
          c.status,
        ]),
        ...filteredDistributions.map((d) => [
          "Distribution",
          `${d.periodStart} → ${d.periodEnd}`,
          "",
          "",
          "",
          "",
          "",
          d.grossAmount,
          d.withheldAmount,
          d.netAmount,
          d.paidAt ?? "",
        ]),
      ],
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Ledger"
        title={`Investor — ${data?.investorDisplayName ?? investorId}`}
        description="All commitments across pools and tiers, every distribution received, running outstanding balance, and YTD earnings."
        actions={
          <Button onClick={exportCsv} disabled={!data}>
            <Download size={16} aria-hidden /> Export CSV
          </Button>
        }
      />

      <Card className="mb-space-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3 mb-space-3">
          <label className="flex flex-col gap-space-1 text-small">
            <span className="text-caption uppercase">Pool</span>
            <Input
              value={poolFilter}
              onChange={(e) => setPoolFilter(e.target.value)}
              placeholder="filter by pool name"
            />
          </label>
          <label className="flex flex-col gap-space-1 text-small">
            <span className="text-caption uppercase">Tier</span>
            <Input
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              placeholder="tier_a / tier_b / ..."
            />
          </label>
          <label className="flex flex-col gap-space-1 text-small">
            <span className="text-caption uppercase">From</span>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="flex flex-col gap-space-1 text-small">
            <span className="text-caption uppercase">To</span>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>

        {data && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3 text-small">
            <Totals label="Total Committed" value={data.totalCommitted} />
            <Totals label="Total Funded" value={data.totalFunded} />
            <Totals label="Total Outstanding" value={data.totalOutstanding} />
            <Totals label="YTD Net" value={data.ytdDistributionsNet} />
          </div>
        )}
      </Card>

      <Card className="mb-space-4">
        <div className="text-body font-semibold mb-space-3">Commitments</div>
        <DataTable
          columns={commitmentCols}
          rows={filteredCommitments}
          loading={query.isLoading}
          error={query.isError ? (query.error as Error).message : null}
          emptyMessage="No commitments match the current filters."
          rowKey={(c) => c.commitmentId}
        />
      </Card>

      <Card>
        <div className="text-body font-semibold mb-space-3">Distributions</div>
        <DataTable
          columns={distributionCols}
          rows={filteredDistributions}
          emptyMessage="No distributions match the current filters."
          rowKey={(d) => d.distributionId}
        />
      </Card>
    </>
  );
}

function Totals({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-border-light p-space-3 bg-white-warm">
      <div className="text-caption uppercase text-gray-mid">{label}</div>
      <div className="text-body font-semibold">{formatMoney(value)}</div>
    </div>
  );
}
